/**
 * Shared hook that builds an EventNode tree from raw events.
 *
 * Handles fixup, treeification, empty-span filtering, source-span attachment
 * (for agent card rendering), and default-collapse computation.
 */

import { useMemo } from "react";

import type { Event, ModelEvent } from "@tsmono/inspect-common/types";

import type { TimelineSpan } from "../timeline/core";
import { groupRetryAttempts } from "../timeline/retryGrouping";
import { correctRetryTimestamps } from "../timeline/retryOrdering";
import { attachSourceSpans } from "../timeline/timelineEventNodes";
import { computeDefaultCollapsedIds } from "../transform/collapse";
import { fixupEventStream } from "../transform/fixups";
import {
  eventFallbackIds,
  filterEmptySpans,
  treeifyEvents,
} from "../transform/treeify";
import { EventNode } from "../types";

export interface EventNodesResult {
  eventNodes: EventNode[];
  defaultCollapsedIds: Record<string, true>;
  retryAttempts: Map<string, ModelEvent[]>;
}

/**
 * Build an EventNode tree from raw events (pure core of `useEventNodes`).
 *
 * Exposed for headless consumers (tests, non-React derivations) that need
 * the exact production pipeline: retry ordering/grouping, fixups,
 * treeification, empty-span filtering, source-span attachment, and
 * default-collapse computation.
 *
 * `allEvents` is the unfiltered event list `events` was drawn from (defaults
 * to `events`): uuid-less events take position-based ids from it, so a node's
 * id is the same whether or not a type filter or lane selection is applied.
 */
export const buildEventNodes = (
  events: Event[],
  running: boolean,
  sourceSpans?: ReadonlyMap<string, TimelineSpan>,
  allEvents: readonly Event[] = events
): EventNodesResult => {
  // Repair retry-inverted ModelEvent timestamps before any downstream
  // sort sees them (treeifyEvents sorts span children by timestamp).
  const orderedEvents = correctRetryTimestamps(events);

  // Fold consecutive same-span failed→success ModelEvent runs into a
  // single visible event; the failed siblings are removed from the
  // stream and surfaced via the retryAttempts map for the chip UI.
  const { events: groupedEvents, attempts: retryAttempts } =
    groupRetryAttempts(orderedEvents);

  // Apply fixups to the event stream
  const resolvedEvents = fixupEventStream(groupedEvents, !running);

  // Build the event tree
  const rawEventTree = treeifyEvents(
    resolvedEvents,
    0,
    eventFallbackIds(allEvents)
  );

  // Attach source span references before filtering so filterEmptySpans
  // can preserve agent card nodes (which have no children by design).
  if (sourceSpans && sourceSpans.size > 0) {
    attachSourceSpans(rawEventTree, sourceSpans);
  }

  const eventNodes = filterEmptySpans(rawEventTree);
  const defaultCollapsedIds = computeDefaultCollapsedIds(eventNodes);

  return { eventNodes, defaultCollapsedIds, retryAttempts };
};

export const useEventNodes = (
  events: Event[],
  running: boolean,
  sourceSpans?: ReadonlyMap<string, TimelineSpan>,
  allEvents?: readonly Event[]
) => {
  return useMemo(
    () => buildEventNodes(events, running, sourceSpans, allEvents),
    [events, running, sourceSpans, allEvents]
  );
};
